import React, { FC, useEffect, useState } from 'react';

import { NativeResources } from '@ariksa/inventory-core';
import { InsightV2ApiInsightV2Request } from '@ariksa/inventory-core/api';
import { Box, Center, HStack, Stack } from '@chakra-ui/react';
import { forEach, isEmpty, map, some } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useDeepCompareEffect } from 'react-use';
import { QueryStatus } from 'services/types';
import { customTheme } from 'theme';

import { InventoryService } from 'api/services';
import { actions } from 'app/containers/PolicyHub/Blueprints/slice';
import {
  renderCloudIcon,
  renderTableHeaderWithLoader,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { DetailsField } from 'components/DataDisplay/Utils/DetailsField';
import { ActionButton } from 'components/DataEntry';
import { SearchBox } from 'components/DataEntry/Input/SearchBox';
import { IconTypes } from 'components/Icons';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { useEnvironmentOptions } from 'containers/App/hooks/useEnvironmentOptions';
import { SubComponentContainer } from 'containers/Compliance/Components/ComplianceTables/SubComponentContainer';
import { renderExpiresIn } from 'containers/PolicyHub/Blueprints/Components/Blueprint/Exceptions/utils';
import { selectBlueprints } from 'containers/PolicyHub/Blueprints/selectors';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { formatDate } from 'utils/date';

interface Props {
  exception: Record<string, any>;
  row: Record<string, any>;
}

export const ResourceExceptionsTable: FC<Props> = props => {
  const { row, exception } = props;
  const { resourceExceptions, entityDetails } = useSelector(selectBlueprints);

  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const { environmentMapping } = useEnvironmentOptions();
  const { accountMapping, renderStackedAccountNameID } = useCloudAccountId();
  const params = useParams<{ id: string }>();

  const { onOpenMetadata } = useActiveResourceContext();
  const { updateActiveResource } = useActiveResourceActions();

  useDeepCompareEffect(() => {
    dispatch(
      actions.getResourceExceptions({
        q: {
          blueprintId: params?.id!,
          page: resourceExceptions.page.info.page_number,
          size: resourceExceptions.page.info.page_size,
          policyId: exception?.policy_id,
          environmentId: row?.environment_id,
        },
      }),
    );
  }, [
    row,
    dispatch,
    searchTerm,
    resourceExceptions.page.info,
    params?.id,
    exception,
  ]);

  useEffect(() => {
    if (isEmpty(resourceExceptions.data)) return;
    const data: Record<string, any[]> = {};
    forEach(resourceExceptions.data, o =>
      !!data[o?.resource_type!]
        ? data[o?.resource_type!].push(o)
        : (data[o.resource_type!] = [o]),
    );

    //get resource details
    forEach(data, (items, key) => {
      const insightPayload: InsightV2ApiInsightV2Request = {
        insightV2Request: {
          resource_type: key as NativeResources,
          resource_uuids: map(items, o => o?.resource_id!),
          account_id: map(items, o => o?.account_id),
        },
      };
      dispatch(
        actions.getEntityDetails({
          q: insightPayload,
          status: QueryStatus.pending,
        }),
      );
      InventoryService.InsightV2.insightV2(insightPayload).then(res => {
        dispatch(
          actions.getEntityDetails({
            q: insightPayload,
            status: QueryStatus.fulfilled,
            data: res.data?.items,
          }),
        );
      });
    });
  }, [resourceExceptions.data, dispatch]);

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Resource</Box>,
      accessor: 'name',
      render: ({ row }) => {
        return (
          <WithResourceIcon resourceType={row?.resource_type}>
            <StackedCell
              upper={row?.resource_name}
              lower={row?.cloud_resource_id}
            />
          </WithResourceIcon>
        );
      },
    },
    {
      header: 'Cloud',
      accessor: 'name',
      render: ({ row }) => {
        const account = accountMapping?.[row?.account_id];
        return (
          <Center>{renderCloudIcon({ provider: account?.cloud_type })}</Center>
        );
      },
    },
    {
      header: 'Environment',
      accessor: 'environment_id',
      align: 'left',
      render: ({ row }) => {
        const env = environmentMapping?.[row?.environment_id];
        return (
          <HStack>
            {/*<Center boxSize={5} color="primary">
              {}
            </Center>*/}
            <Box>{env?.name}</Box>
          </HStack>
        );
      },
    },
    {
      header: 'Accounts',
      accessor: 'account_id',
      align: 'left',
      render: ({ value }) => renderStackedAccountNameID(value),
    },
    {
      header: renderTableHeaderWithLoader(
        'Deployed In',
        some(entityDetails, { isLoading: true }),
      ),
      align: 'left',
      render: ({ row }) => {
        const resource =
          entityDetails?.[row?.resource_type]?.data?.[row?.resource_id];
        return (
          <StackedCell upper={resource?.vpc_id} lower={resource?.region} />
        );
      },
    },
    {
      header: 'Expires in',
      align: 'left',
      render: renderExpiresIn,
      styles: { width: '180px', cell: { maxWidth: '180px' } },
    },
    {
      header: 'Actions',
      render: ({ row }) => (
        <Center>
          <ActionButton
            iconType={IconTypes.Summary}
            label="Show resource details"
            onClick={() => {
              updateActiveResource({
                resourceType: row?.resource_type,
                uuid: row?.resource_id,
                accountId: row?.account_id,
                resourceId: row?.cloud_resource_id,
              });
              onOpenMetadata();
            }}
          />
        </Center>
      ),
      styles: { width: '180px', cell: { maxWidth: '180px' } },
    },
  ];

  const subComponentStyles = { value: { color: 'black' } };

  return (
    <>
      <Table
        data={resourceExceptions.data}
        columns={columns}
        isLoading={resourceExceptions.isLoading}
        pagination={{
          totalCount: resourceExceptions.page.totalCount,
          pageInfo: resourceExceptions.page.info,
          onChange: pageInfo =>
            dispatch(actions.updateResourceExceptionsPagination(pageInfo)),
        }}
        subComponent={({ row, expanded }) => (
          <SubComponentContainer>
            <Stack spacing={5}>
              <DetailsField
                label={'Reason for Exception'}
                value={row?.description}
                isInline={false}
                showColon={false}
                styles={subComponentStyles}
              />
              <DetailsField
                label="Duration"
                value={!!row?.nearest_expire_at ? '' : 'Forever'}
                styles={subComponentStyles}
              />
              <DetailsField
                label="Expires In"
                value={
                  !!row?.nearest_expire_at ? renderExpiresIn({ row }) : 'Never'
                }
                styles={subComponentStyles}
              />
              <DetailsField
                label="Applied By"
                value={
                  <HStack spacing={1}>
                    <Box>{row?.last_updated_by ?? '-'} at</Box>
                    <Box color={customTheme.colors.gray['250']}>
                      {formatDate(row?.last_updated_at)}
                    </Box>
                  </HStack>
                }
                styles={subComponentStyles}
              />
            </Stack>
          </SubComponentContainer>
        )}
      />
    </>
  );
};
